import { useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { t } from '../lib/i18n';
import { useAuth, type MfaState } from '../lib/auth-context';

/**
 * F1-018 — Gate que intercepta usuarios sujeitos ao regime dual de MFA.
 *
 * Regras (ADR-005 §2-bis + B.20 D-015):
 *   - regime 'disabled' ou mfa ja enabled: renderiza o app normalmente.
 *   - regime 'immediate' e !enabled: **redireciona** para /mfa/setup
 *     (owner com descendentes — sem carencia, sem banner).
 *   - regime 'grace_period' e !enabled:
 *       - se grace_until > now: banner permite navegacao, CTA leva ao setup.
 *       - se grace_until <= now: redireciona compulsoriamente para /mfa/setup.
 *
 * Banner pode ser dispensado sessao-a-sessao via state local (dismiss local).
 * O servidor continua a enforcement independente de dismiss.
 */

function formatDate(iso: string | null): string {
  if (iso === null) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function isMfaBlocking(mfa: MfaState, now: Date = new Date()): boolean {
  if (mfa.enabled) return false;
  if (mfa.regime === 'disabled') return false;
  if (mfa.regime === 'immediate') return true;
  // grace_period
  if (mfa.graceUntil === null) return false;
  const until = new Date(mfa.graceUntil).getTime();
  if (Number.isNaN(until)) return false;
  return now.getTime() >= until;
}

export function isMfaBannerVisible(mfa: MfaState, now: Date = new Date()): boolean {
  if (mfa.enabled) return false;
  if (mfa.regime !== 'grace_period') return false;
  if (mfa.graceUntil === null) return false;
  const until = new Date(mfa.graceUntil).getTime();
  if (Number.isNaN(until)) return false;
  return now.getTime() < until;
}

export function MfaGate({ children }: { children: ReactNode }): JSX.Element {
  const { mfaState } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const onSetupRoute = location.pathname.startsWith('/mfa/');

  if (!onSetupRoute && isMfaBlocking(mfaState)) {
    return <Navigate to="/mfa/setup" replace />;
  }

  const showBanner = !onSetupRoute && !dismissed && isMfaBannerVisible(mfaState);

  return (
    <>
      {showBanner ? (
        <div className="mfa-grace-banner" role="status" aria-live="polite">
          <span>
            {t('mfa.gate.graceBanner').replace('{date}', formatDate(mfaState.graceUntil))}
          </span>
          <a className="mfa-grace-cta" href="/mfa/setup">
            {t('mfa.gate.graceCta')}
          </a>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label={t('mfa.gate.dismissBanner')}
          >
            {t('mfa.gate.dismissBanner')}
          </button>
        </div>
      ) : null}
      {children}
    </>
  );
}
